<template>
  <article class="indicator" :class="{ 'is-rated': value.level != null }">
    <header class="ind-head">
      <span class="ind-index">{{ String(index + 1).padStart(2, '0') }}</span>
      <div class="ind-title">
        <h3>{{ indicator.name }}</h3>
        <p>{{ indicator.goal }}</p>
      </div>
      <span class="ind-domain">{{ domainName }}</span>
    </header>

    <!-- 判定问题：勾选后推荐档位，可手动覆盖 -->
    <section class="block">
      <div class="block-head">
        <span class="block-title">判定问题</span>
        <span class="block-tip">全部勾选后自动套用推荐档位（黄色）；也可直接点选档位（蓝色），点选会清空勾选</span>
      </div>
      <ul class="q-list">
        <li v-for="(q, i) in indicator.questions" :key="i" class="q-item">
          <span class="q-index">{{ circled(i) }}</span>
          <span class="q-text">{{ q }}</span>
          <div class="q-ops">
            <button
              v-for="opt in answerOptions"
              :key="opt.value"
              type="button"
              class="q-op"
              :class="[`is-${opt.type}`, { 'is-active': value.answers[i] === opt.value }]"
              @click="setAnswer(i, opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </li>
      </ul>
    </section>

    <!-- 评分档位 -->
    <section class="block">
      <div class="block-head">
        <span class="block-title">评分档位</span>
        <span v-if="suggested != null" class="block-tip">
          依据勾选推荐 <b>{{ suggested }}</b> 档
        </span>
        <span v-else class="block-tip">共 {{ scoreSteps.length }} 档，允许半分</span>
      </div>
      <div class="lv-list">
        <button
          v-for="step in scoreSteps"
          :key="step"
          type="button"
          class="lv-item"
          :class="{
            'is-active': value.level === step && !(allAnswered && value.level === suggested),
            'is-suggested': allAnswered && step === suggested && value.level === suggested
          }"
          @click="setLevel(step)"
        >
          <span class="lv-num">{{ step }}</span>
          <span class="lv-text">{{ levelText(step) }}</span>
        </button>
      </div>
    </section>

    <!-- 评分依据：v2.2.0 起默认关闭显示（ENABLE_SCORE_NOTE=false）。
         数据模型 value.note 与下方更新逻辑保留，开关打开即恢复 -->
    <section v-if="ENABLE_SCORE_NOTE" class="block">
      <div class="block-head">
        <span class="block-title">评分依据</span>
        <span class="block-tip">说明为什么给该分而非更高 / 更低，保证跨评价者一致</span>
      </div>
      <el-input
        :model-value="value.note"
        type="textarea"
        :rows="2"
        resize="vertical"
        maxlength="300"
        show-word-limit
        placeholder="例如：已发布数据战略文件并经决策层审批，但尚未纳入年度复盘，故给 3 档"
        @update:model-value="(v) => emit('update', { note: v })"
      />
    </section>

    <!-- 佐证材料：代码已就绪，由 ENABLE_EVIDENCE 开关控制渲染 -->
    <EvidencePanel
      v-if="ENABLE_EVIDENCE"
      :evidence="evidence"
      :indicator-name="indicator.name"
      :files="value.files || []"
      @update-files="(files) => emit('update', { files })"
    />
  </article>
</template>

<script setup>
import { computed, watch } from 'vue'
import EvidencePanel from './EvidencePanel.vue'
import { ENABLE_EVIDENCE, ENABLE_SCORE_NOTE, SCORE_STEPS } from '../data/index.js'
import { ANSWER_OPTIONS, suggestLevel } from '../utils/scoring.js'

const props = defineProps({
  indicator: { type: Object, required: true },
  domainName: { type: String, default: '' },
  index: { type: Number, default: 0 },
  value: {
    type: Object,
    default: () => ({ level: null, answers: {}, note: '', files: [] })
  },
  evidence: { type: Object, default: null }
})

const emit = defineEmits(['update'])

const answerOptions = ANSWER_OPTIONS
const scoreSteps = SCORE_STEPS

const suggested = computed(() => suggestLevel(props.indicator, props.value.answers))

// 判定问题与评分档位「一致化」：这是自评，两者本就是同一件事，但视觉区分来源（决策 2.2.1）。
// ① 全部判定问题作答 → 推荐档位以「黄色」高亮（is-suggested），即该项得分；
//    此状态会覆盖任何手动点击（问题才是权威自评来源）。
// ② 直接点击评分档位 → 以「蓝色」高亮（is-active），并清空判定问题（answers={}），
//    点击的档位即该项得分。
const allAnswered = computed(() =>
  props.indicator.questions.length > 0 &&
  props.indicator.questions.every((_, i) => props.value.answers[i])
)

watch(
  [allAnswered, suggested],
  () => {
    // 全答且当前档位≠推荐值时，自动套用推荐档位（黄色）；否则不动
    if (allAnswered.value && suggested.value != null && props.value.level !== suggested.value) {
      emit('update', { level: suggested.value })
    }
  }
)

const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']
function circled (i) {
  return CIRCLED[i] || `${i + 1}.`
}

/** 档位文案：整数档取附录原文，半分档说明其位于相邻两档之间 */
function levelText (step) {
  if (step === 0) return '未起步：无任何实践，仅零星试点或口头 / 纸质传递'
  const hit = props.indicator.levels.find((l) => l.level === step)
  if (hit) {
    const range = hit.range ? `（${hit.range}）` : ''
    return `${range}${hit.text}`
  }
  const lower = Math.floor(step)
  return `介于 ${lower} 档与 ${lower + 1} 档之间：接近上一档但未完全达标`
}

function setAnswer (i, val) {
  const answers = { ...props.value.answers }
  // 再次点击同一选项即取消，便于快速调整
  if (answers[i] === val) delete answers[i]
  else answers[i] = val
  emit('update', { answers })
}

function setLevel (step) {
  // 直接点击评分档位 = 手动评分（蓝色）：该档位即得分，并清空判定问题（决策 2.2.1-②）
  if (props.value.level === step) {
    // 再次点击已选档位即取消，回到未评
    emit('update', { level: null, answers: {} })
  } else {
    emit('update', { level: step, answers: {} })
  }
}
</script>

<style lang="scss" scoped>
.indicator {
  padding: 22px 24px 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  transition: border-color 0.24s ease, box-shadow 0.24s ease;

  &:hover {
    border-color: rgba(0, 113, 227, 0.28);
  }

  &.is-rated {
    border-color: rgba(0, 113, 227, 0.45);
    box-shadow: 0 0 0 2px rgba(0, 113, 227, 0.07);
  }
}

.ind-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.ind-index {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  font-size: 12.5px;
  font-weight: 600;
  color: #0071e3;
  background: rgba(0, 113, 227, 0.10);
  border-radius: 9px;
}

.ind-title {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 2px 0 0;
    font-size: 17px;
    font-weight: 600;
    color: #1d1d1f;
  }

  p {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.7;
    color: #6e6e73;
  }
}

.ind-domain {
  flex: 0 0 auto;
  padding: 4px 11px;
  font-size: 11.5px;
  color: #6e6e73;
  background: rgba(120, 120, 128, 0.10);
  border-radius: 999px;
  white-space: nowrap;
}

.block {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.block-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #1d1d1f;
}

.block-tip {
  font-size: 12px;
  color: #86868b;

  b {
    color: #0071e3;
  }
}

.q-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.q-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;

  & + & {
    border-top: 1px dashed rgba(0, 0, 0, 0.06);
  }
}

.q-index {
  flex: 0 0 auto;
  color: #0071e3;
  font-size: 14px;
}

.q-text {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  line-height: 1.7;
  color: #3a3a3c;
}

.q-ops {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
}

.q-op {
  padding: 4px 12px;
  font-size: 12.5px;
  color: #6e6e73;
  background: rgba(120, 120, 128, 0.08);
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 120, 128, 0.16);
  }

  &.is-active.is-success {
    color: #ffffff;
    background: #34c759;
  }

  &.is-active.is-danger {
    color: #ffffff;
    background: #ff3b30;
  }

  &.is-active.is-info {
    color: #ffffff;
    background: #8e8e93;
  }
}

.lv-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.lv-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 13px;
  text-align: left;
  background: rgba(120, 120, 128, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 113, 227, 0.06);
    border-color: rgba(0, 113, 227, 0.32);
  }

  &.is-suggested {
    border-color: rgba(255, 149, 0, 0.55);
    background: rgba(255, 149, 0, 0.06);
  }

  &.is-active {
    color: #ffffff;
    background: #0071e3;
    border-color: #0071e3;

    .lv-num,
    .lv-text {
      color: #ffffff;
    }
  }
}

.lv-num {
  flex: 0 0 auto;
  min-width: 22px;
  font-size: 14px;
  font-weight: 700;
  color: #0071e3;
}

.lv-text {
  flex: 1;
  font-size: 12.5px;
  line-height: 1.6;
  color: #3a3a3c;
}

@media (max-width: 768px) {
  .indicator {
    padding: 18px 16px 16px;
  }

  .q-item {
    flex-wrap: wrap;
  }

  .q-ops {
    width: 100%;
    padding-left: 24px;
  }

  .ind-domain {
    display: none;
  }
}
</style>
